import { useEffect, type ReactNode } from "react";
import { useLocation } from "react-router-dom";
import { AppSidebar } from "./AppSidebar";
import { AppHeader, type Breadcrumb } from "./AppHeader";
import { LegacyTopBar } from "./LegacyTopBar";
import { cn } from "@/lib/utils";
import { SidebarProvider } from "@/contexts/SidebarContext";
import { useSidebarContext } from "@/hooks/use-sidebar-context";
import { useLayoutStyle } from "@/hooks/use-layout-style";

interface AppLayoutProps {
  title: string;
  breadcrumbs?: Breadcrumb[];
  children: ReactNode;
}

// title/breadcrumbs chegam como props explícitas de quem renderiza a página, em vez
// de um mapa central pathname -> config (o SilpiV2 fazia isso e precisava de uma
// cadeia de regex para rotas dinâmicas). Quando o roteador real existir (passo 0.6+),
// isso pode migrar para `handle` de cada <Route> + useMatches() do react-router —
// mais robusto que um mapa mantido à mão, e sem regex.
function AppLayoutContent({ title, breadcrumbs, children }: AppLayoutProps) {
  const { style } = useLayoutStyle();
  const { collapsed, setCollapsed, mobileOpen, setMobileOpen } = useSidebarContext();
  const location = useLocation();

  useEffect(() => {
    setMobileOpen(false);
  }, [location.pathname, setMobileOpen]);

  // Skin "menu antigo": réplica do header horizontal do legado (ver LegacyTopBar).
  // Sem breadcrumbs/título aqui de propósito — o app original também não tinha
  // essa barra, cada página exibia seu próprio título de conteúdo.
  if (style === "legacy") {
    return (
      <div className="min-h-screen bg-background">
        <LegacyTopBar />
        <main className="p-4 lg:p-6 animate-fade-in">{children}</main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      {mobileOpen && (
        <div className="fixed inset-0 bg-black/50 z-30 lg:hidden" onClick={() => setMobileOpen(false)} />
      )}

      <div className={cn("lg:block", mobileOpen ? "block" : "hidden")}>
        <AppSidebar collapsed={collapsed} onToggle={() => setCollapsed(!collapsed)} />
      </div>

      <div className={cn("min-h-screen transition-all duration-300 flex flex-col", collapsed ? "lg:pl-16" : "lg:pl-72")}>
        <AppHeader title={title} breadcrumbs={breadcrumbs} showMenuButton onMenuClick={() => setMobileOpen(true)} />

        <main className="flex-1 p-4 lg:p-6 animate-fade-in">{children}</main>
      </div>
    </div>
  );
}

export function AppLayout(props: AppLayoutProps) {
  return (
    <SidebarProvider>
      <AppLayoutContent {...props} />
    </SidebarProvider>
  );
}
